import { useMemo, useState } from "react";
import { Calculator } from "lucide-react";
import { ContactForm } from "./ContactForm";


/** Richtprijzen per m² per bouwmethode — zelfde bandbreedtes als de prijstabel. */
const methoden = [
  { id: "hsb", label: "Houtskeletbouw (prefab)", min: 1800, max: 2600 },
  { id: "metselwerk", label: "Traditioneel metselwerk", min: 2200, max: 3200 },
  { id: "serre", label: "Serre of glazen uitbouw", min: 2500, max: 3800 },
  { id: "casco", label: "Alleen ruwbouw (casco)", min: 1200, max: 1700 },
] as const;

/** Afwerkniveau werkt als factor op de richtprijs per m². */
const niveaus = [
  { id: "basis", label: "Basis", factor: 0.9 },
  { id: "standaard", label: "Standaard", factor: 1 },
  { id: "luxe", label: "Luxe / maatwerk", factor: 1.2 },
] as const;

const format = (value: number) =>
  new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value);

/** Per m² is al afgerond op € 50, dus het totaal is exact per m² × oppervlakte. */
const euro = format;
const euroPerM2 = format;

export function AanbouwCalculator() {
  const [oppervlakte, setOppervlakte] = useState(20);
  const [methode, setMethode] = useState<string>("hsb");
  const [niveau, setNiveau] = useState<string>("standaard");

  const resultaat = useMemo(() => {
    const m = methoden.find((item) => item.id === methode) ?? methoden[0];
    const n = niveaus.find((item) => item.id === niveau) ?? niveaus[1];
    // Grotere uitbouw = lagere prijs per m²: vaste kosten verdelen zich over meer meters.
    const schaal = oppervlakte >= 30 ? 0.93 : oppervlakte >= 20 ? 0.97 : 1;
    // Eerst per m² afronden op € 50, zodat totaal = per m² × oppervlakte blijft kloppen.
    const perM2Min = Math.round((m.min * n.factor * schaal) / 50) * 50;
    const perM2Max = Math.round((m.max * n.factor * schaal) / 50) * 50;
    return {
      perM2Min,
      perM2Max,
      totaalMin: perM2Min * oppervlakte,
      totaalMax: perM2Max * oppervlakte,
    };
  }, [oppervlakte, methode, niveau]);

  // Onderwerp + bericht lopen automatisch mee met de gekozen waarden.
  const prefill = useMemo(() => {
    const methodeLabel = (methoden.find((item) => item.id === methode) ?? methoden[0]).label;
    const niveauLabel = (niveaus.find((item) => item.id === niveau) ?? niveaus[1]).label;
    return {
      onderwerp: `Aanbouw ${oppervlakte} m² — ${methodeLabel}`,
      bericht: [
        `Hoi Fedor, ik heb de rekenhulp op de site gebruikt voor een aanbouw:`,
        ``,
        `• Oppervlakte: ${oppervlakte} m²`,
        `• Bouwmethode: ${methodeLabel}`,
        `• Afwerkniveau: ${niveauLabel}`,
        `• Richtprijs per m²: ${euroPerM2(resultaat.perM2Min)} – ${euroPerM2(resultaat.perM2Max)}`,
        `• Richtprijs totaal: ${euro(resultaat.totaalMin)} – ${euro(resultaat.totaalMax)}`,
        ``,
        `Kun je hier een vrijblijvende offerte van maken?`,
      ].join("\n"),
    };
  }, [oppervlakte, methode, niveau, resultaat]);



  return (
    <div className="rounded-2xl border border-border/60 bg-card p-5 sm:p-7">
      <div className="flex items-center gap-2">
        <Calculator className="h-5 w-5 text-primary" aria-hidden="true" />
        <h3 className="font-heading text-lg font-semibold text-foreground">
          Aanbouw kosten berekenen
        </h3>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        Vul je oppervlakte, bouwmethode en afwerkniveau in en zie direct een richtprijs per m² en
        het totaalbedrag.
      </p>

      <div className="mt-6 space-y-6">
        <div>
          <label
            htmlFor="aanbouw-oppervlakte"
            className="flex items-baseline justify-between gap-3 text-sm font-semibold text-foreground"
          >
            <span>Oppervlakte van de uitbouw</span>
            <span className="font-heading text-primary">{oppervlakte} m²</span>
          </label>
          <input
            id="aanbouw-oppervlakte"
            type="range"
            min={6}
            max={50}
            step={1}
            value={oppervlakte}
            onChange={(event) => setOppervlakte(Number(event.target.value))}
            className="mt-3 h-11 w-full cursor-pointer accent-primary"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>6 m²</span>
            <span>50 m²</span>
          </div>
        </div>

        <fieldset>
          <legend className="text-sm font-semibold text-foreground">Bouwmethode</legend>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {methoden.map((item) => (
              <label
                key={item.id}
                className={`flex min-h-12 cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                  methode === item.id
                    ? "border-primary bg-primary/5 font-semibold text-foreground"
                    : "border-input text-muted-foreground hover:bg-accent"
                }`}
              >
                <input
                  type="radio"
                  name="aanbouw-methode"
                  value={item.id}
                  checked={methode === item.id}
                  onChange={() => setMethode(item.id)}
                  className="accent-primary"
                />
                <span className="min-w-0">{item.label}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend className="text-sm font-semibold text-foreground">Afwerkniveau</legend>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {niveaus.map((item) => (
              <label
                key={item.id}
                className={`flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-md border px-2 py-2 text-center text-sm transition-colors ${
                  niveau === item.id
                    ? "border-primary bg-primary/5 font-semibold text-foreground"
                    : "border-input text-muted-foreground hover:bg-accent"
                }`}
              >
                <input
                  type="radio"
                  name="aanbouw-niveau"
                  value={item.id}
                  checked={niveau === item.id}
                  onChange={() => setNiveau(item.id)}
                  className="sr-only"
                />
                <span>{item.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
      </div>

      <div
        aria-live="polite"
        className="mt-6 rounded-xl border border-border/60 bg-secondary/30 p-4 sm:p-5"
      >
        <p className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Richtprijs voor {oppervlakte} m²
        </p>
        <p className="mt-1 font-heading text-2xl font-bold leading-tight tracking-tight text-primary sm:text-3xl">
          {euro(resultaat.totaalMin)} – {euro(resultaat.totaalMax)}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Dat is ongeveer{" "}
          <strong className="text-foreground">
            {euroPerM2(resultaat.perM2Min)} – {euroPerM2(resultaat.perM2Max)} per m²
          </strong>
          , inclusief btw.
        </p>
        <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
          Indicatie op basis van projecten in de regio Dordrecht. Vergunning, extra fundering op
          palen en grote schuifpuien kunnen de prijs verhogen; een exacte prijs volgt uit een gratis
          opname bij je thuis.
        </p>
      </div>

      <div id="berekening-offerte" className="mt-8 scroll-mt-24 border-t border-border/60 pt-6">
        <h4 className="font-heading text-lg font-semibold text-foreground">
          Deze berekening als offerte aanvragen
        </h4>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Je gekozen oppervlakte, bouwmethode en richtprijs staan al in het formulier. Vul je
          gegevens aan en we nemen snel contact op voor een gratis opname.
        </p>
        <div className="mt-5">
          <ContactForm prefill={prefill} />
        </div>
      </div>
    </div>

  );
}
